<script setup lang="ts">
import LeHeading from "@dsk/components/Heading/LeHeading.vue";
import LeIconButton from "@dsk/components/IconButton/LeIconButton.vue";
import LeTooltip from "@dsk/components/LeTooltip.vue";
import LeBackspace from "@dsk/icons/LeBackspace.vue";
import LeCross from "@dsk/icons/LeCross.vue";
import LeKeyboard from "@dsk/icons/LeKeyboard.vue";
import { ArabicKeys, GuldfuglKeyboard, type Keys as KeyboardKeys, RussianKeys } from "@ordbogen-npm/guldfugl-keyboards";
import { computed, type ComputedRef } from "vue";
import { useI18n } from "vue-i18n";

import { config } from "@/config";
import { vMatomoEvent } from "@/directives";
import { DictionariesHelper, MatomoHelper } from "@/helpers";
import { type KeyboardState, useFloatingKeyboardStore } from "@/stores";
import { useSearchStore } from "@/stores";
import { MatomoEventAction, MatomoEventCategory } from "@/types";

const emit = defineEmits<{
  key: [key: string];
  backspace: [];
  close: [];
}>();

const { t } = useI18n();

const floatingKeyboardStore = useFloatingKeyboardStore();

const EVENT_LABEL_OPEN = "open";
const EVENT_LABEL_CLOSE = "close";
const EVENT_LABEL_KEY_PRESS = "key-press";

const toggleButtonId = "guldfugl-keyboard-toggle-button";
defineExpose({
  toggleButtonId
});

const searchStore = useSearchStore();

const usableKeyboard = computed<"arabic" | "russian" | undefined>(() => {
  if (config.app.isMobile) {
    return undefined;
  }

  if (
    DictionariesHelper.ARABIC_DICTIONARY_IDS.includes(searchStore.selectedDictionaryId ?? "") ||
    [searchStore.filter?.sourceLanguage?.code, searchStore.filter?.targetLanguage?.code].includes("ar")
  ) {
    return "arabic";
  }

  if (
    DictionariesHelper.RUSSIAN_DICTIONARY_IDS.includes(searchStore.selectedDictionaryId ?? "") ||
    [searchStore.filter?.sourceLanguage?.code, searchStore.filter?.targetLanguage?.code].includes("ru")
  ) {
    return "russian";
  }

  return undefined;
});

type Keyboard = {
  keys: KeyboardKeys;
  show: ComputedRef<boolean>;
  state: KeyboardState;
  texts: {
    open: string;
    close: string;
    header: string;
  };
  events: {
    category: MatomoEventCategory;
    action: MatomoEventAction;
  };
};

const keyboards: Keyboard[] = [
  {
    keys: ArabicKeys,
    show: computed(() => usableKeyboard.value === "arabic"),
    state: floatingKeyboardStore.getState("arabic"),
    texts: {
      open: t("le-arabic-keyboard.keyboard-tooltip.text", { action: t("le-arabic-keyboard.keyboard-tooltip.on") }),
      close: t("le-arabic-keyboard.keyboard-tooltip.text", { action: t("le-arabic-keyboard.keyboard-tooltip.off") }),
      header: t("le-arabic-keyboard.header-text")
    },
    events: {
      category: MatomoEventCategory.ArabicKeyboard,
      action: MatomoEventAction.ArabicKeyboard
    }
  },
  {
    keys: RussianKeys,
    show: computed(() => usableKeyboard.value === "russian"),
    state: floatingKeyboardStore.getState("russian"),
    texts: {
      open: t("le-russian-keyboard.keyboard-tooltip.text", { action: t("le-russian-keyboard.keyboard-tooltip.on") }),
      close: t("le-russian-keyboard.keyboard-tooltip.text", { action: t("le-russian-keyboard.keyboard-tooltip.off") }),
      header: t("le-russian-keyboard.header-text")
    },
    events: {
      category: MatomoEventCategory.RussianKeyboard,
      action: MatomoEventAction.RussianKeyboard
    }
  }
];

const setIsOpen = (state: KeyboardState, isOpen: boolean) => {
  state.isOpen = isOpen;

  if (isOpen === false) {
    emit("close");
  }
};

const onKey = (keyboard: Keyboard, key: string) => {
  MatomoHelper.sendEvent(keyboard.events.category, keyboard.events.action, EVENT_LABEL_KEY_PRESS);
  emit("key", key);
};

const onBackspace = (keyboard: Keyboard) => {
  MatomoHelper.sendEvent(keyboard.events.category, keyboard.events.action, EVENT_LABEL_KEY_PRESS);
  emit("backspace");
};
</script>

<template>
  <div class="le-keyboards">
    <template v-for="(keyboard, index) in keyboards" :key="index">
      <GuldfuglKeyboard
        v-if="keyboard.show.value"
        :keys="keyboard.keys"
        :is-open="keyboard.state.isOpen"
        :position-x="keyboard.state.posX"
        :position-y="keyboard.state.posY"
        :y-offset="70"
        @open="setIsOpen(keyboard.state, true)"
        @close="setIsOpen(keyboard.state, false)"
        @position-x="keyboard.state.posX = $event"
        @position-y="keyboard.state.posY = $event"
        @key="onKey(keyboard, $event)"
        @backspace="onBackspace(keyboard)"
      >
        <template #toggle="{ onToggle }">
          <LeTooltip :text="keyboard.state.isOpen ? keyboard.texts.close : keyboard.texts.open" :margin-bottom="10">
            <LeIconButton
              :id="toggleButtonId"
              v-matomo-event:click="{
                category: keyboard.events.category,
                action: keyboard.events.action,
                label: keyboard.state.isOpen ? EVENT_LABEL_OPEN : EVENT_LABEL_CLOSE
              }"
              :aria-label="keyboard.state.isOpen ? keyboard.texts.close : keyboard.texts.open"
              :aria-pressed="keyboard.state.isOpen"
              @click="onToggle()"
            >
              <LeKeyboard role="presentation" width="16" height="16" />
            </LeIconButton>
          </LeTooltip>
        </template>

        <template #header="{ id }">
          <LeHeading :id="id" tag="h3" size="m">{{ keyboard.texts.header }}</LeHeading>
        </template>
        <template #close>
          <LeIconButton
            v-matomo-event:click="{
              category: keyboard.events.category,
              action: keyboard.events.action,
              label: EVENT_LABEL_CLOSE
            }"
            type="button"
            @click="setIsOpen(keyboard.state, false)"
          >
            <LeCross role="presentation" />
          </LeIconButton>
        </template>

        <template #backspace>
          <LeBackspace role="presentation" width="24" height="24" />
        </template>
      </GuldfuglKeyboard>
    </template>
  </div>
</template>

<style lang="less" scoped>
@import "@dsk/styles/all";
@import "@/assets/styles/z-index.less";

.le-keyboards {
  ::v-deep(.guldfugl-keyboards) {
    z-index: @le-keyboards__z-index;
    border-radius: 4px;
    background-color: @colors[@secondary][100];
    box-shadow:
      0px 4px 30px rgba(0, 0, 0, 0.2),
      0px 18px 11px -7px rgba(0, 0, 0, 0.1),
      0px 12px 20px -7px rgba(0, 0, 0, 0.1);
  }

  ::v-deep(.guldfugl-keyboards__header) {
    padding: 20px 24px;
    background-color: @colors[@neutrals][100];
    color: @colors[@neutrals][500];
  }

  ::v-deep(.guldfugl-keyboards__body) {
    padding: 20px 8px;
  }

  ::v-deep(.guldfugl-keyboards__button) {
    border: 1px solid @colors[@secondary][600];
    background-color: @colors[@neutrals][100];

    &:hover {
      border-color: @colors[@secondary][600];
      background-color: @colors[@secondary][200];
    }

    &:focus {
      border-color: @colors[@secondary][600];
      background-color: @colors[@neutrals][100];
      box-shadow: 0 0 0 4px rgba(176, 209, 203, 0.4);
    }
  }
}
</style>
